import { Map as ImmutableMap } from 'immutable';
import { v4 as uuid } from 'uuid';

import { selectShouldHideBusinessFromAiChat } from 'app-components/flux/ai-chat/aiChatSelectors';
import { getBusinessCoaIdsSet, getCategoriesById } from 'app-components/flux/categories/categoriesSelectors';
import { createDialog } from 'app-components/flux/root-ui/rootUiActions';
import { mkRootUiData } from 'app-components/flux/root-ui/rootUiTypes';
import { getTags } from 'app-components/flux/tags/tagsSelectors';
import { clearState } from 'app-components/flux/transaction-rules/transactionRulesForm';
import { getRulesMatchingPredicate } from 'app-components/flux/transaction-rules/transactionRulesSelectors';
import { transactionRulesSelectors } from 'app-components/flux/transaction-rules/transactionRulesSlice';
import type { TransactionRuleType } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import { getLogger } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { safeParse } from '../../aiChatUtils';
import { returnToolResponse } from '../llmToolsFramework';
import type { ToolDefinition, ToolMessage } from '../llmToolsTypes';
import { findCategoryIdByName } from '../utils';
import { type LlmRuleData, normalizeLlmRuleData } from './ruleLlmData';

const log = getLogger('flux/ai-chat/tools/handlers/rules');

type RuleSearchCriteria = {
  payeeName?: string;
  categoryName?: string;
  amount?: number;
  amountType?: 'INCOME' | 'EXPENSE';
  accountType?: 'BUSINESS' | 'PERSONAL';
};

const createRuleMatchPredicate = (criteria: RuleSearchCriteria, categories: ReturnType<typeof getCategoriesById>) => {
  const { payeeName, categoryName, amount, amountType, accountType } = criteria;
  const normalizedPayee = payeeName?.toLowerCase().trim();
  const normalizedCategoryName = categoryName?.toLowerCase().trim();

  return (rule: TransactionRuleType): boolean => {
    log.debug('createRuleMatchPredicate: checking rule', {
      ruleId: rule.id,
      rulePayee: rule.payeeSource?.payeeMatchingCriteria?.[0]?.payeeName,
    });

    if (normalizedPayee) {
      const rulePayee = rule.payeeSource?.payeeMatchingCriteria?.[0]?.payeeName?.toLowerCase().trim();
      log.debug('createRuleMatchPredicate: comparing payee', { normalizedPayee, rulePayee });
      const payeeMatches = rulePayee && (rulePayee.includes(normalizedPayee) || normalizedPayee.includes(rulePayee));
      if (!payeeMatches) {
        log.debug('createRuleMatchPredicate: payee did not match');
        return false;
      }
      log.debug('createRuleMatchPredicate: payee matched');
    }

    if (normalizedCategoryName) {
      if (!rule.coaTarget?.id) {
        log.debug('createRuleMatchPredicate: rule has no coaTarget, skipping');
        return false;
      }
      const ruleCategoryName = categories
        .find((c) => c.id === rule.coaTarget?.id)
        ?.name?.toLowerCase()
        .trim();
      log.debug('createRuleMatchPredicate: comparing category', { normalizedCategoryName, ruleCategoryName });
      const categoryMatches =
        ruleCategoryName &&
        (ruleCategoryName.includes(normalizedCategoryName) || normalizedCategoryName.includes(ruleCategoryName));
      if (!categoryMatches) {
        log.debug('createRuleMatchPredicate: category did not match');
        return false;
      }
      log.debug('createRuleMatchPredicate: category matched');
    }

    if (amount !== undefined && amount !== 0) {
      const ruleAmount = rule.amountSource?.amount1;
      const ruleAmountType = rule.amountSource?.amountType;
      const amountMatches = ruleAmount === amount && (!amountType || ruleAmountType === amountType);
      if (!amountMatches) return false;
    }

    if (accountType && rule.accountSource?.accountSourceType) {
      if (rule.accountSource.accountSourceType !== accountType) return false;
    }

    log.debug('createRuleMatchPredicate: all criteria matched');
    return true;
  };
};

const findMatchingRule = (criteria: RuleSearchCriteria, state: RootState): TransactionRuleType | undefined => {
  const { payeeName, categoryName, amount, accountType } = criteria;

  const allRules = transactionRulesSelectors.selectAll(state);
  log.debug('findMatchingRule: searching', { criteria, rulesCount: allRules.length });

  if (!payeeName && !categoryName && amount === undefined && !accountType) {
    log.debug('findMatchingRule: no search criteria provided');
    return undefined;
  }

  const categories = getCategoriesById(state);
  const predicate = createRuleMatchPredicate(criteria, categories);
  const matches = getRulesMatchingPredicate(state, predicate);
  log.debug('findMatchingRule: found matches', { count: matches.length });
  return matches[0];
};

const formatRuleForAI = (rule: TransactionRuleType, categories: ReturnType<typeof getCategoriesById>) => {
  const categoryName = rule.coaTarget?.id ? categories.find((c) => c.id === rule.coaTarget?.id)?.name : undefined;

  return {
    id: rule.id,
    payeeCondition: rule.payeeSource?.payeeMatchingCriteria?.[0]?.payeeName,
    payeeTarget: rule.payeeTarget,
    categoryTarget: categoryName,
    amountCondition: rule.amountSource,
    memoTarget: rule.memoTarget,
    reviewedTarget: rule.reviewedTarget,
    excludedTarget: rule.excludedTarget,
    modifiedAt: rule.modifiedAt,
  };
};

const resolveLlmDataToRuleFields = (data: LlmRuleData, state: RootState) => {
  const categories = getCategoriesById(state);
  const normalizedCategoryName = data.categoryName?.toLowerCase().trim();
  const categoryMatch = normalizedCategoryName
    ? categories.find((cat) => cat.name?.toLowerCase().trim() === normalizedCategoryName)
    : undefined;

  const targetCoa =
    categoryMatch?.id && categoryMatch?.name
      ? { id: categoryMatch.id, name: categoryMatch.name, type: 'CATEGORY' as const }
      : undefined;

  const allTags = getTags(state);
  const tagIds = data.tags
    ?.map((tagName) => {
      const normalizedTagName = tagName.toLowerCase().trim();
      const tag = allTags?.find((t) => t.name?.toLowerCase().trim() === normalizedTagName);
      return tag?.id;
    })
    .filter((id): id is string => !!id);

  return { targetCoa, tagIds };
};

const buildRuleFromLlmData = (data: LlmRuleData, state: RootState): TransactionRuleType => {
  const { targetCoa, tagIds } = resolveLlmDataToRuleFields(data, state);

  return {
    ...clearState,
    payeeSource: data.payeeName
      ? {
          payeeType: 'STATEMENT',
          payeeMatchingCriteria: [{ payeeName: data.payeeName, payeeMatchingOperator: 'CONTAINS' }],
        }
      : undefined,
    payeeTarget: data.renamePayeeTo,
    amountSource:
      data.amount !== undefined
        ? {
            amountMatchingOperator: 'EQUALS',
            amount1: Math.abs(data.amount),
            amountType: data.amountType || (data.amount < 0 ? 'EXPENSE' : 'INCOME'),
          }
        : undefined,
    accountSource: data.accountType ? { accountSourceType: data.accountType } : undefined,
    tagsTarget: tagIds?.length ? tagIds : undefined,
    memoTarget: data.memo,
    reviewedTarget: data.markAsReviewed,
    excludedTarget:
      data.excludeFromReports || data.excludeFromSpending
        ? { isExcludedFromReports: !!data.excludeFromReports, isExcludedFromF2S: !!data.excludeFromSpending }
        : undefined,
    coaTarget: targetCoa,
  };
};

const mergeRuleWithLlmData = (
  existingRule: TransactionRuleType,
  data: LlmRuleData,
  state: RootState,
): TransactionRuleType => {
  const { targetCoa, tagIds } = resolveLlmDataToRuleFields(data, state);

  return {
    ...existingRule,
    payeeSource: data.payeeName
      ? {
          payeeType: 'STATEMENT',
          payeeMatchingCriteria: [{ payeeName: data.payeeName, payeeMatchingOperator: 'CONTAINS' }],
        }
      : existingRule.payeeSource,
    payeeTarget: data.renamePayeeTo !== undefined ? data.renamePayeeTo : existingRule.payeeTarget,
    amountSource:
      data.amount !== undefined
        ? {
            amountMatchingOperator: 'EQUALS',
            amount1: Math.abs(data.amount),
            amountType: data.amountType || (data.amount < 0 ? 'EXPENSE' : 'INCOME'),
          }
        : existingRule.amountSource,
    accountSource: data.accountType ? { accountSourceType: data.accountType } : existingRule.accountSource,
    tagsTarget: tagIds?.length ? tagIds : existingRule.tagsTarget,
    memoTarget: data.memo !== undefined ? data.memo : existingRule.memoTarget,
    reviewedTarget: data.markAsReviewed !== undefined ? data.markAsReviewed : existingRule.reviewedTarget,
    excludedTarget:
      data.excludeFromReports !== undefined || data.excludeFromSpending !== undefined
        ? {
            isExcludedFromReports:
              data.excludeFromReports ?? existingRule.excludedTarget?.isExcludedFromReports ?? false,
            isExcludedFromF2S: data.excludeFromSpending ?? existingRule.excludedTarget?.isExcludedFromF2S ?? false,
          }
        : existingRule.excludedTarget,
    coaTarget: targetCoa ?? existingRule.coaTarget,
  };
};

const transactionRulesListDefinition: ToolDefinition = {
  name: 'transaction_rules_list',
  description:
    'Get the list of transaction rules. A transaction rule automatically applies changes to transactions matching certain conditions (payee, amount, category). Returns rule conditions and targets. Be concise and brief in your response.',
  parameters: {
    type: 'object',
    properties: {},
    required: [],
  },
};

const upsertTransactionRuleDefinition: ToolDefinition = {
  name: 'upsert_transaction_rule',
  description: `Create or edit a transaction rule. Opens a dialog for user to review.

FLOW:
1. Call with rule criteria (payeeName, categoryName, etc.)
2. If existing rule found: returns rule info - ASK user: edit existing or create new?
3. Based on user response, call again with:
   - userConfirmedEdit=true to edit existing rule
   - forceCreate=true to create new rule despite existing one
4. If no existing rule: creates new rule directly

IF conditions (at least one required):
- payeeName: Payee contains this text
- categoryName: Category name to match
- amount + amountType: Amount equals value (INCOME/EXPENSE)
- accountType: BUSINESS or PERSONAL

THEN actions:
- renamePayeeTo: Rename payee
- categoryName: Set category
- tags: Array of tag names
- memo: Notes to add
- markAsReviewed: true/false
- excludeFromReports: true/false
- excludeFromSpending: true/false

Be concise and brief in your response.`,
  parameters: {
    type: 'object',
    properties: {
      payeeName: {
        type: 'string',
        description: 'IF: Payee name to match',
      },
      amount: { type: 'number', description: 'IF: Amount to match' },
      amountType: {
        type: 'string',
        enum: ['INCOME', 'EXPENSE'],
        description: 'IF: Match income or expense transactions',
      },
      accountType: {
        type: 'string',
        enum: ['BUSINESS', 'PERSONAL'],
        description: 'IF: Match transactions from business or personal accounts only',
      },
      renamePayeeTo: { type: 'string', description: 'THEN: Rename payee to this value' },
      categoryName: { type: 'string', description: 'THEN: Set category to this' },
      tags: {
        type: 'array',
        items: { type: 'string' },
        description: 'THEN: Tag names to add to matching transactions',
      },
      memo: { type: 'string', description: 'THEN: Notes to add to matching transactions' },
      markAsReviewed: { type: 'boolean', description: 'THEN: Mark as reviewed' },
      excludeFromReports: { type: 'boolean', description: 'THEN: Exclude from reports' },
      excludeFromSpending: { type: 'boolean', description: 'THEN: Exclude from spending analysis' },
      userConfirmedEdit: {
        type: 'boolean',
        description: 'Set to true ONLY after user confirms they want to edit existing rule',
      },
      forceCreate: {
        type: 'boolean',
        description: 'Set to true ONLY after user confirms they want to create new rule despite existing one',
      },
    },
    required: [],
  },
};

export const rulesTools = {
  transaction_rules_list: {
    definition: transactionRulesListDefinition,
    handler: (msg: ToolMessage) => {
      const state = store?.getState() as RootState;
      const rules = transactionRulesSelectors.selectAll(state);
      const categories = getCategoriesById(state);

      log.debug('transaction_rules_list: Returning rules', { count: rules.length });

      if (rules.length === 0) {
        return returnToolResponse(msg, {
          rules: [],
          message: 'No transaction rules found. Would you like to create one?',
        });
      }

      const formattedRules = rules.slice(0, 20).map((rule) => formatRuleForAI(rule, categories));

      return returnToolResponse(msg, {
        rules: formattedRules,
        count: rules.length,
        showing: formattedRules.length,
      });
    },
  },

  upsert_transaction_rule: {
    definition: upsertTransactionRuleDefinition,
    handler: (msg: ToolMessage) => {
      const args = safeParse(msg.arguments) || {};

      const {
        payeeName,
        amount,
        amountType,
        accountType,
        renamePayeeTo,
        categoryName,
        tags,
        memo,
        markAsReviewed,
        excludeFromReports,
        excludeFromSpending,
      } = args;

      const state = store?.getState() as RootState;
      const existingRule = findMatchingRule({ payeeName, categoryName, amount, amountType, accountType }, state);
      const categories = getCategoriesById(state);

      if (categoryName && selectShouldHideBusinessFromAiChat(state)) {
        const targetCategoryId = findCategoryIdByName(state, categoryName);
        if (targetCategoryId && getBusinessCoaIdsSet(state).has(targetCategoryId)) {
          return returnToolResponse(msg, {
            success: false,
            error:
              'Transaction rules that assign business categories are not supported via chat. Use the dedicated business features in Quicken instead.',
          });
        }
      }

      if (existingRule && !args.userConfirmedEdit && !args.forceCreate) {
        const existingPayee = existingRule.payeeSource?.payeeMatchingCriteria?.[0]?.payeeName;
        const existingCategory = existingRule.coaTarget?.id
          ? categories.find((c) => c.id === existingRule.coaTarget?.id)?.name
          : undefined;

        log.debug('upsert_transaction_rule: Found existing rule, asking for confirmation', {
          existingPayee,
          existingCategory,
        });

        return returnToolResponse(msg, {
          existingRuleFound: true,
          existingRule: {
            payeeCondition: existingPayee,
            categoryTarget: existingCategory,
          },
          message: `Found existing rule for "${existingPayee}" with category "${existingCategory}". Ask user: edit existing rule or create new one?`,
          nextStep: 'Call again with userConfirmedEdit=true to edit, or forceCreate=true to create new',
        });
      }

      const shouldEdit = existingRule && !args.forceCreate;
      const operation = shouldEdit ? 'update' : 'create';

      log.debug('upsert_transaction_rule:', { operation, existingRuleFound: !!existingRule, payeeName, categoryName });

      const llmData = normalizeLlmRuleData({
        payeeName,
        amount,
        amountType,
        accountType,
        renamePayeeTo,
        categoryName,
        tags,
        memo,
        markAsReviewed,
        excludeFromReports,
        excludeFromSpending,
      });

      const rule = shouldEdit
        ? mergeRuleWithLlmData(existingRule, llmData, state)
        : buildRuleFromLlmData(llmData, state);

      return new Promise((resolve) => {
        const handleClose = (newRule?: TransactionRuleType) => {
          if (newRule) {
            log.debug('upsert_transaction_rule: Success', { operation, ruleId: newRule.id });
            resolve(
              returnToolResponse(msg, { success: true, message: `Transaction rule ${operation}d successfully.` }),
            );
          } else {
            log.debug('upsert_transaction_rule: Cancelled');
            resolve(
              returnToolResponse(msg, {
                success: false,
                cancelled: true,
                message:
                  'User closed the dialog without saving. Do NOT retry or open another dialog. Ask user if they need anything else.',
              }),
            );
          }
        };

        const dialogData = mkRootUiData({
          type: 'LLM_RULE',
          id: `llm-rule-${uuid()}`,
          allowNesting: true,
          props: ImmutableMap({ data: args, rule, hasAIEdits: shouldEdit, callback: handleClose }),
        });

        log.debug('upsert_transaction_rule: Dispatching dialog');
        store?.dispatch(createDialog(dialogData));
      });
    },
  },
};
